import type { CallReadyAnswer, CallReadyFindingsModel, CallReadyStageContext, CallReadyLeadFact, CallReadyConfirmItem, CallReadyValidationBlock } from '../findings/callReadyFindings';
import {
  buildComparableProjectsPresentation,
  type ComparableProjectsPresentationModel,
} from '../findings/comparableProjectsPresentation';
import type { EconomicResearchResultSnapshot } from '../../types/economicResearch';
import { hashEconomicModelInput } from './modelUtils';

/**
 * v4: export the stage-aware answer order and sourced validation, without presenter notes.
 */
export const ECONOMIC_BRIEF_SCHEMA_VERSION = 4 as const;

export interface EconomicBriefLine {
  label: string;
  value: string;
  basis: 'recorded' | 'recommended' | 'not-determined';
  basisNote: string;
  nextStep?: string;
}

export interface EconomicBriefRisk {
  area: string;
  title: string;
  state: 'evidenced' | 'not-assessed';
  severity: 'significant' | 'noted';
  finding: string;
  source: string;
  action: string;
}

export interface EconomicBriefSection {
  id: string;
  title: string;
  status: CallReadyAnswer['status'];
  basis: readonly string[];
  summary: string;
  /** Labelled answer lines, printed below the spoken summary. */
  lines?: readonly EconomicBriefLine[];
  /** Evidenced risks with actions, and topics not yet assessed. */
  risks?: readonly EconomicBriefRisk[];
  lineBalance?: string;
  clearTopics?: readonly string[];
  leadFacts?: readonly CallReadyLeadFact[];
  confirm?: readonly Omit<CallReadyConfirmItem, 'route'>[];
  confirmLabel?: string;
  validation?: CallReadyValidationBlock;
  details: readonly { label: string; value: string; group?: string }[];
  sources: readonly { label: string; url?: string; vintage?: string; excerpt?: string; locator?: string }[];
  limitations: readonly string[];
}

export interface EconomicContributionBriefModel {
  schemaVersion: typeof ECONOMIC_BRIEF_SCHEMA_VERSION;
  title: 'Data Center Economic Contribution Brief';
  stage?: CallReadyStageContext;
  siteId: string;
  siteName: string;
  generatedAt: string;
  snapshotId: string;
  inputHash: string;
  sourceCatalogVersion: string;
  authoritySnapshots: readonly EconomicBriefAuthoritySnapshot[];
  distribution: EconomicBriefDistribution;
  sections: readonly EconomicBriefSection[];
  comparableProjects?: ComparableProjectsPresentationModel;
  disclosure: string;
}

export interface EconomicBriefAuthoritySnapshot {
  kind: 'economic-screening' | 'comparable-research';
  snapshotId: string;
  generatedAt: string;
  inputHash: string;
  sourceCatalogVersion: string;
}

export type EconomicBriefDistribution =
  | { kind: 'internal-screening' }
  | { kind: 'external'; reviewedBy: string; reviewedAt: string; purpose: string };

export function buildEconomicContributionBriefModel(
  callSheet: CallReadyFindingsModel,
  distribution: EconomicBriefDistribution = { kind: 'internal-screening' },
  researchSnapshot?: EconomicResearchResultSnapshot | null,
): EconomicContributionBriefModel {
  const snapshot = callSheet.economicSnapshot;
  const researchReady = (researchSnapshot?.policyValidRecords.length ?? 0) > 0;
  const screenReady = snapshot !== null && callSheet.economicSnapshotAuthority === 'server-authored';
  if (!screenReady && !researchReady) {
    throw new Error('A current server-authored economic screening or comparable-research snapshot is required for export.');
  }
  if (distribution.kind === 'external' && (
    !distribution.reviewedBy.trim()
    || !distribution.purpose.trim()
    || !Number.isFinite(Date.parse(distribution.reviewedAt))
  )) {
    throw new Error('External distribution requires a named review, review date, and stated purpose.');
  }
  const byId = new Map(callSheet.questions.map((question) => [question.id, question]));
  const comparableProjects = buildComparableProjectsPresentation(researchSnapshot);
  const researchComparableSummary = comparableProjects
    ? `Research found ${comparableProjects.peerCount} policy-valid comparable ${comparableProjects.peerCount === 1 ? 'project' : 'projects'}. The closest reviewed peers cover ${comparableProjects.coverageLine}.`
    : null;
  const briefQuestionIds = callSheet.questions.filter(answer => answer.id !== 'answer-basis' && (screenReady || answer.id === 'comparables')).map(answer => answer.id);
  const sections = briefQuestionIds.flatMap((id) => {
    const answer = byId.get(id);
    if (!answer) return [];
    const researchBackedComparable = id === 'comparables' && comparableProjects !== null;
    const researchOnlyComparable = researchBackedComparable && !screenReady;
    return [{
      id: answer.id,
      title: answer.question,
      status: researchBackedComparable ? 'ready' as const : answer.status,
      basis: researchOnlyComparable
        ? ['public-record']
        : researchBackedComparable
        ? [...new Set([...answer.bases.filter((basis) => basis !== 'unavailable'), 'public-record'])]
        : answer.bases,
      summary: id === 'comparables' && researchComparableSummary ? researchComparableSummary : answer.summary,
      ...(answer.lines && answer.lines.length > 0
        ? {
            lines: answer.lines.map((line) => ({
              label: line.label,
              value: line.value,
              basis: line.basis,
              basisNote: line.basisNote,
              ...(line.nextStep ? { nextStep: line.nextStep } : {}),
            })),
          }
        : {}),
      ...(answer.risks && answer.risks.length > 0
        ? {
            risks: answer.risks.map((risk) => ({
              area: risk.area,
              title: risk.title,
              state: risk.state,
              severity: risk.severity,
              finding: risk.finding,
              source: risk.source,
              action: risk.action,
            })),
          }
        : {}),
      ...(answer.lineBalance ? { lineBalance: answer.lineBalance } : {}),
      ...(answer.clearTopics?.length ? { clearTopics: answer.clearTopics } : {}),
      ...(answer.leadFacts?.length ? { leadFacts: answer.leadFacts } : {}),
      ...(answer.confirm?.length ? { confirm: answer.confirm.map(({ label, note }) => ({ label, note })), confirmLabel: answer.confirmLabel } : {}),
      ...(answer.validation ? { validation: answer.validation } : {}),
      // A locally computed preview is not export authority. The specialized
      // comparable presentation below carries the research-backed facts and
      // sources, so do not let local screening details or evidence cross into
      // a research-only brief through the call-sheet answer.
      details: researchOnlyComparable ? [] : answer.details,
      sources: (researchOnlyComparable ? [] : answer.evidence).map((source) => ({
        label: source.label,
        url: source.url,
        vintage: source.vintage,
        excerpt: source.excerpt,
        locator: source.locator,
      })),
      limitations: researchBackedComparable ? [] : [answer.nextInput, answer.expandsWith ? `Expands with: ${answer.expandsWith}` : undefined].filter((item): item is string => item !== undefined),
    }];
  });
  const authoritySnapshots: EconomicBriefAuthoritySnapshot[] = [
    ...(screenReady ? [{
      kind: 'economic-screening' as const,
      snapshotId: snapshot.snapshotId,
      generatedAt: snapshot.generatedAt,
      inputHash: snapshot.inputHash,
      sourceCatalogVersion: snapshot.sourceCatalogVersion,
    }] : []),
    ...(researchReady && researchSnapshot ? [{
      kind: 'comparable-research' as const,
      snapshotId: researchSnapshot.snapshotId,
      generatedAt: researchSnapshot.generatedAt,
      inputHash: researchSnapshot.siteFingerprint,
      sourceCatalogVersion: `economic-research-result-v${researchSnapshot.schemaVersion}`,
    }] : []),
  ];
  const latestAuthority = authoritySnapshots.reduce<EconomicBriefAuthoritySnapshot | null>((latest, authority) => (
    !latest || Date.parse(authority.generatedAt) > Date.parse(latest.generatedAt) ? authority : latest
  ), null);
  if (!latestAuthority) {
    throw new Error('The export snapshot is missing required identity metadata.');
  }
  const compositeIdentity = authoritySnapshots.length > 1
    ? hashEconomicModelInput({ schemaVersion: ECONOMIC_BRIEF_SCHEMA_VERSION, authoritySnapshots })
    : null;
  const snapshotId = compositeIdentity
    ? `economic-brief-composite-${compositeIdentity.slice(0, 20)}`
    : latestAuthority.snapshotId;
  const inputHash = compositeIdentity ?? latestAuthority.inputHash;
  const sourceCatalogVersion = authoritySnapshots.length > 1
    ? authoritySnapshots.map((authority) => `${authority.kind}:${authority.sourceCatalogVersion}`).join('|')
    : latestAuthority.sourceCatalogVersion;
  return {
    schemaVersion: ECONOMIC_BRIEF_SCHEMA_VERSION,
    title: 'Data Center Economic Contribution Brief',
    siteId: callSheet.siteId,
    siteName: callSheet.siteName,
    generatedAt: latestAuthority.generatedAt,
    snapshotId,
    inputHash,
    sourceCatalogVersion,
    authoritySnapshots,
    distribution,
    ...(callSheet.stage ? { stage: callSheet.stage } : {}),
    sections,
    ...(comparableProjects ? { comparableProjects } : {}),
    disclosure: `${distribution.kind === 'internal-screening' ? 'INTERNAL SCREENING ONLY. ' : `EXTERNAL DISTRIBUTION REVIEWED BY ${distribution.reviewedBy} ON ${distribution.reviewedAt}. `}NOT A COMMITMENT. ${screenReady
      ? 'Early-stage screening, not an IMPLAN, RIMS II, tax-eligibility, legal, or negotiated-incentive analysis. Recorded power and cooling lines come from the site record; recommended lines are platform screening recommendations, not an adopted design. Water-use bands are modeled scenarios, not permitted demand or measured site use. Direct permanent employment, construction activity, public workforce context, comparable observations, and user-defined fiscal scenarios retain their distinct definitions and evidence bases.'
      : 'Comparable-project public-record research only. No site-specific jobs, workforce, construction, fiscal, or water screening is included. Comparable observations are evidence about reviewed projects, not forecasts for this site.'}`,
  };
}
